import { Modal, Skeleton } from '@mui/material'
import styled from 'styled-components'
import { CrossIcon } from '../../assets/icons/cross'
import { useEffect, useRef } from 'react'
import { FirstDiagrams, SecondDiagrams, ThirdDiagrams } from '../../assets/icons/diagramms'
import { useTranslation } from 'react-i18next'

const DisclaimerContainer = styled.div`
  background: #f8f9fa;
  border-left: 4px solid #366F72;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 20px;
`;

const DisclaimerTitle = styled.p`
  font-size: 14px;
  font-weight: 700;
  margin: 0px;
`;

const DisclaimerText = styled.p`
  font-family: Roboto;
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-primary, #1a1a1a);
  margin: 0px;
`;

const Background = styled.div`
  background-color: #fff;
  overflow: hidden;
`
const Title = styled.p`
  color: var(--text-main, #1a1a1a);
  /* Header/L Semibold */
  font-family: Roboto;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 36px; /* 150% */
  letter-spacing: 0.48px;
  margin: 0px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;

  svg {
    cursor: pointer;
  }
`

const Box = styled.div`
  margin-bottom: 15px;
  margin-top: 20px;
  display: flex;
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: center;
    height: fit-content;
  }

`

export const SkeletonContainer = styled.div`
  margin-top: 20px;
  height: 200px;
  margin-right: 20px;
`

const TextBody = styled.div``
const Heading = styled.p`
  color: var(--text-main, #1a1a1a);
  /* Header/M */
  font-family: Roboto;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 32px; /* 177.778% */
  letter-spacing: 0.36px;
  margin: 0px;
`
const Paragraph = styled.span`
  color: var(--text-main, #1a1a1a);
  /* Paragraph/M */
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 24px; /* 171.429% */
  letter-spacing: 0.28px;
`

const ColorList = styled.ul`
  margin: 10px 0 0 0;
  padding-left: 0px;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ColorListItem = styled.li`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  color: var(--text-main, #1a1a1a);
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 24px;
  letter-spacing: 0.28px;
  margin-top: 8px;
  list-style: none;

  &:first-child {
    margin-top: 0;
  }

  &::before {
    content: none;
  }
`

const ColorDot = styled.span<{ color: string }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`

const HowToInterpretCharts = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation()
  const ref = useRef(null)

  document.onkeydown = (e) => {
    const { keyCode: code } = e
    if (+code == 27) {
      document.onkeydown = null
      onClose()
    }
  }

  return (
    <Background ref={ref} className='paddModal' data-animch={3}>
      <Header>
        <Title>{t('howToInterpretCharts')}</Title>
        <CrossIcon onClick={onClose} />
      </Header>
      <TextBody style={{ marginTop: '20px'}}>
        <Heading>{t('howToInterpretCharts.whatIsThisScore')}</Heading>
        <Paragraph>
          {t('howToInterpretCharts.whatIsThisScoreDescription')}
        </Paragraph>

        <Heading style={{ marginTop: '24px' }}>{t('howToInterpretCharts.howToInterpretColors')}</Heading>
        <Paragraph>
          {t('howToInterpretCharts.colorsDescription')}
        </Paragraph>
        
        <ColorList>
          <ColorListItem>
            <ColorDot color='#218B8F' />
            <Paragraph as='span'>
              <strong>{t('howToInterpretCharts.optimal')}:</strong> {t('howToInterpretCharts.optimalDescription')}
            </Paragraph>
          </ColorListItem>
          <ColorListItem>
            <ColorDot color='#93DAB8' />
            <Paragraph as='span'>
              <strong>{t('howToInterpretCharts.suboptimal')}:</strong> {t('howToInterpretCharts.suboptimalDescription')}
            </Paragraph>
          </ColorListItem>
          <ColorListItem>
            <ColorDot color='#FF9000' />
            <Paragraph as='span'>
              <strong>{t('howToInterpretCharts.moderateRisk')}:</strong> {t('howToInterpretCharts.moderateRiskDescription')}
            </Paragraph>
          </ColorListItem>
          <ColorListItem>
            <ColorDot color='#D83F3D' />
            <Paragraph as='span'>
              <strong>{t('howToInterpretCharts.attention')}:</strong> {t('howToInterpretCharts.attentionDescription')}
            </Paragraph>
          </ColorListItem>
        </ColorList>

        <Heading style={{ marginTop: '24px' }}>{t('howToInterpretCharts.whyScoreLower')}</Heading>
        <Paragraph>
          {t('howToInterpretCharts.whyScoreLowerDescription')}
        </Paragraph>

        <DisclaimerContainer style={{ marginTop: '24px' }}>
          <DisclaimerTitle>{t('howToInterpretCharts.disclaimer')}</DisclaimerTitle>
          <DisclaimerText>
            {t('howToInterpretCharts.disclaimerDescription')}
          </DisclaimerText>
        </DisclaimerContainer>
      </TextBody>
    </Background>
  )
}

export default HowToInterpretCharts
